/**
 * Environment configuration. Reads `import.meta.env` (Vite's build-time-replaced env),
 * normalizes defaults, and exposes them as a typed `env` object.
 *
 * Origins are never build-time configuration: one bundle serves every `brand.<slug>.<base>`
 * host and every whitelabel `brand.<domain>` host, so no `VITE_*` var may bake in an origin.
 * The API is same-origin (`src/api/client.ts` sets `baseUrl: ''`) and the centralized-login
 * URL is resolved at runtime from `GET /api/branding/by-hostname`, paired to the caller's own
 * domain (`src/auth/loginKickoff.ts`).
 */
export interface AppEnv {
  envLabel: string;
  supportedLocales: readonly string[];
}

export const env: AppEnv = {
  envLabel: import.meta.env.VITE_ENV_LABEL?.trim() || 'local',
  supportedLocales: (import.meta.env.VITE_SUPPORTED_LOCALES?.trim() || 'en,es,fr,pt')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0),
};

/**
 * Throw a clear, var-naming error when any required var is empty. Pure and exported so
 * tests can exercise it directly without flipping `import.meta.env`. Currently a no-op —
 * there are no required origins left to validate — but kept as the boot-time contract
 * `main`-side boot calls and `env.test.ts` exercises.
 */
export function validateEnv(_e: AppEnv): void {
  // No required vars at present.
}

// Fail loud at boot for built bundles only. `vite dev` and Vitest have
// `import.meta.env.PROD === false`, so they never throw at import — tests exercise
// `validateEnv` directly.
if (import.meta.env.PROD) {
  validateEnv(env);
}
